import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MODEL_TIERS,
  modelPricingListSchema,
  resolveModelPricing,
  tierFromPrices,
  type AppSettings,
  type DiscoveredEntities,
  type ModelPricing,
  type ModelProvider,
  type ModelTier,
} from '@ace-tracker/shared';
import { Button } from '../Button';
import { TOOL_LABELS } from '../Sidebar';
import {
  AmountInput,
  Card,
  IconButton,
  SaveBar,
  SelectInput,
  TextInput,
  amountToString,
  stringToAmount,
} from './formParts';
import { useSaveSettings } from './useSaveSettings';
import { PriceFeedPanel } from './PriceFeedPanel';

interface DraftRow {
  key: number;
  model: string;
  provider: ModelProvider;
  input: string;
  output: string;
  cacheRead: string;
  cacheWrite: string;
  effectiveFrom: string;
  tier: ModelTier | '';
  contextWindow: string;
  notes: string;
}

const PROVIDER_LABELS: Record<ModelProvider, string> = { anthropic: 'Anthropic', openai: 'OpenAI', other: 'Other' };
const TIER_LABELS: Record<ModelTier, string> = { fast: 'Fast', mid: 'Mid', frontier: 'Frontier' };

function autoTier(output: string): ModelTier {
  const value = Number(output);
  return tierFromPrices({ inputPerMillion: 0, outputPerMillion: Number.isFinite(value) ? value : 0 });
}

let nextKey = 1;

function toDraft(models: ModelPricing[]): DraftRow[] {
  return models.map((m) => ({
    key: nextKey++,
    model: m.model,
    provider: m.provider,
    input: amountToString(m.inputPerMillion),
    output: amountToString(m.outputPerMillion),
    cacheRead: amountToString(m.cacheReadPerMillion),
    cacheWrite: amountToString(m.cacheWritePerMillion),
    effectiveFrom: m.effectiveFrom ?? '',
    tier: m.tier ?? '',
    contextWindow: m.contextWindow === undefined ? '' : String(m.contextWindow),
    notes: m.notes ?? '',
  }));
}

/** Raw (unvalidated) conversion — the schema check on save reports what's wrong. */
function toModels(rows: DraftRow[]): ModelPricing[] {
  return rows.map((r) => ({
    model: r.model.trim(),
    provider: r.provider,
    inputPerMillion: Number(r.input.trim() === '' ? NaN : r.input),
    outputPerMillion: Number(r.output.trim() === '' ? NaN : r.output),
    cacheReadPerMillion: stringToAmount(r.cacheRead),
    cacheWritePerMillion: stringToAmount(r.cacheWrite),
    effectiveFrom: r.effectiveFrom.trim() || undefined,
    tier: r.tier || undefined,
    contextWindow: r.contextWindow.trim() === '' ? undefined : Number(r.contextWindow),
    notes: r.notes.trim() || undefined,
  }));
}

function describeIssue(rows: DraftRow[], issue: { path: (string | number)[]; message: string }): string {
  const [index, field] = issue.path;
  if (typeof index !== 'number') return issue.message;
  const name = rows[index]?.model.trim() || `row ${index + 1}`;
  const label: Record<string, string> = {
    model: 'model id',
    inputPerMillion: 'input price',
    outputPerMillion: 'output price',
    cacheReadPerMillion: 'cache-read price',
    cacheWritePerMillion: 'cache-write price',
    effectiveFrom: 'price from',
    contextWindow: 'context window (whole tokens, 1,000 or more)',
    notes: 'notes',
  };
  return `${name}: ${label[String(field)] ?? 'value'} — ${issue.message === 'Expected number, received nan' ? 'enter a number' : issue.message}`;
}

export function ModelPricingSettings({
  settings,
  discovered,
  discoveryError,
}: {
  settings: AppSettings;
  discovered?: DiscoveredEntities;
  discoveryError?: string;
}) {
  const baseline = settings.models;
  const currency = settings.cost.currency;
  const [rows, setRows] = useState<DraftRow[]>(() => toDraft(baseline));
  const [search, setSearch] = useState('');
  const [provider, setProvider] = useState<ModelProvider | 'all'>('all');
  const [validationError, setValidationError] = useState<string>();
  const { save, saving, error, savedAt, reset } = useSaveSettings('models');
  const firstInputRef = useRef<HTMLInputElement>(null);
  const focusNewRow = useRef(false);

  useEffect(() => setRows(toDraft(baseline)), [baseline]);
  useEffect(() => {
    if (focusNewRow.current) {
      focusNewRow.current = false;
      firstInputRef.current?.focus();
    }
  });

  const draftModels = toModels(rows);
  const baselineJson = useMemo(() => JSON.stringify(toModels(toDraft(baseline))), [baseline]);
  const dirty = JSON.stringify(draftModels) !== baselineJson;

  // Token-priced tools only — Copilot is priced per AIU (Cost tab), not by this table.
  const tokenModels = useMemo(() => (discovered?.models ?? []).filter((m) => m.tool !== 'copilot'), [discovered]);
  const usage = (() => {
    const byRow = new Map<string, number>();
    const unpriced: { model: string; tool: 'claude-code' | 'codex'; events: number }[] = [];
    const priceable = draftModels.filter((m) => m.model);
    for (const m of tokenModels) {
      const { pricedAs } = resolveModelPricing(m.model, priceable, settings.cost.fallbackPricing);
      if (pricedAs) byRow.set(pricedAs.toLowerCase(), (byRow.get(pricedAs.toLowerCase()) ?? 0) + m.events);
      else unpriced.push({ model: m.model, tool: m.tool as 'claude-code' | 'codex', events: m.events });
    }
    return { byRow, unpriced };
  })();
  const unpricedKey = [...new Set(usage.unpriced.map((m) => m.model))].sort().join('\u0000');
  const unpricedIds = useMemo(() => (unpricedKey ? unpricedKey.split('\u0000') : []), [unpricedKey]);

  const visible = rows.filter((r) => {
    if (provider !== 'all' && r.provider !== provider) return false;
    const q = search.trim().toLowerCase();
    return !q || r.model.toLowerCase().includes(q) || r.notes.toLowerCase().includes(q);
  });

  function update(key: number, patch: Partial<DraftRow>) {
    setValidationError(undefined);
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addRow(model = '', providerHint: ModelProvider = 'other') {
    const fb = settings.cost.fallbackPricing;
    setValidationError(undefined);
    setSearch('');
    setProvider('all');
    focusNewRow.current = !model;
    setRows((rs) => [
      {
        key: nextKey++,
        model,
        provider: providerHint,
        input: amountToString(fb.inputPerMillion),
        output: amountToString(fb.outputPerMillion),
        cacheRead: amountToString(fb.cacheReadPerMillion),
        cacheWrite: providerHint === 'openai' ? '' : amountToString(fb.cacheWritePerMillion),
        effectiveFrom: '',
        tier: '',
        contextWindow: '',
        notes: model ? 'Added from telemetry — prices copied from the fallback rate; update them' : '',
      },
      ...rs,
    ]);
  }

  function handleSave() {
    const parsed = modelPricingListSchema.safeParse(draftModels);
    if (!parsed.success) {
      setValidationError(describeIssue(rows, parsed.error.issues[0]));
      return;
    }
    save(parsed.data);
  }

  return (
    <div className="space-y-5">
      {(usage.unpriced.length > 0 || discoveryError) && (
        <div className="rounded-card border border-warning bg-warn-bg p-5">
          <h3 className="flex items-center gap-2 text-sm font-medium text-text-default">
            <i aria-hidden="true" className="fa-solid fa-triangle-exclamation text-warn-ink" />
            {discoveryError ? 'Could not check telemetry for unpriced models' : 'Models in use without a price'}
          </h3>
          {discoveryError ? (
            <p className="mt-1 text-xs text-text-muted">{discoveryError}</p>
          ) : (
            <>
              <p className="mt-1 text-xs text-text-muted">
                Reported in the last {discovered?.days} days but matching no row below, so they're costed at the fallback rate
                (Cost tab). Add a row to price them properly.
              </p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {usage.unpriced.map((m) => (
                  <li key={`${m.tool}:${m.model}`}>
                    <button
                      type="button"
                      onClick={() => addRow(m.model, m.tool === 'codex' ? 'openai' : 'anthropic')}
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-input border border-border bg-surface px-3 text-sm text-text-default hover:border-primary-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue"
                    >
                      <i aria-hidden="true" className="fa-solid fa-plus text-xs text-primary-blue" />
                      <span className="font-mono text-xs">{m.model}</span>
                      <span className="text-[11px] text-text-faint">
                        {TOOL_LABELS[m.tool]} · {m.events.toLocaleString()} requests
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <PriceFeedPanel settings={settings} unpricedModels={unpricedIds} dirty={dirty} />

      <Card
        title="Model pricing"
        description={
          <>
            Prices in <b className="font-medium">{currency} per million tokens</b>, used for Claude Code and Codex indicative cost.
            A dated model id (e.g. <span className="font-mono">claude-sonnet-4-5-20250929</span>) uses its base row. Cache read
            prices Claude Code cache reads and Codex cached input; cache write prices Claude Code cache creation (OpenAI has no
            cache-write charge — leave it empty). A model can have several rows with different <b className="font-medium">From</b>{' '}
            dates: each day is costed at the row in force that day (an empty date applies from the start). GitHub Copilot is
            priced per AIU on the Cost tab.
          </>
        }
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[14rem] flex-1">
            <label htmlFor="model-search" className="sr-only">
              Search models
            </label>
            <TextInput
              id="model-search"
              type="search"
              placeholder="Search model id or notes"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-44">
            <label htmlFor="model-provider-filter" className="sr-only">
              Provider
            </label>
            <SelectInput
              id="model-provider-filter"
              value={provider}
              onChange={(e) => setProvider(e.target.value as ModelProvider | 'all')}
            >
              <option value="all">All providers</option>
              {Object.entries(PROVIDER_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectInput>
          </div>
          <Button type="button" variant="secondary" onClick={() => addRow()}>
            <i aria-hidden="true" className="fa-solid fa-plus" />
            Add model
          </Button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[1530px] text-left text-sm">
            <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
              <tr>
                <th className="pb-2.5 pr-2">Model id</th>
                <th className="w-[8.5rem] pb-2.5 pr-2">Provider</th>
                <th className="w-[6.5rem] pb-2.5 pr-2 text-right">Input</th>
                <th className="w-[6.5rem] pb-2.5 pr-2 text-right">Output</th>
                <th className="w-[6.5rem] pb-2.5 pr-2 text-right">Cache read</th>
                <th className="w-[6.5rem] pb-2.5 pr-2 text-right">Cache write</th>
                <th className="w-[9.5rem] pb-2.5 pr-2" title="First day this row's prices apply; empty = from the start">From</th>
                <th className="w-[8.5rem] pb-2.5 pr-2" title="Fast, mid or frontier, for the model tier mix. Auto = from the output price">Tier</th>
                <th className="w-[8rem] pb-2.5 pr-2 text-right" title="Context window in tokens, for context pressure. Empty = from the price feed">Context window</th>
                <th className="w-[16%] pb-2.5 pr-2">Notes</th>
                <th className="w-[6rem] pb-2.5 pr-2 text-right" title="Usage in retained telemetry, up to the last 90 days">Used (≤90d)</th>
                <th className="w-11 pb-2.5">
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => {
                const name = r.model.trim() || 'new model';
                const used = usage.byRow.get(r.model.trim().toLowerCase());
                return (
                  <tr key={r.key} className="border-b border-border last:border-0">
                    <td className="py-2 pr-2">
                      <TextInput
                        ref={i === 0 ? firstInputRef : undefined}
                        aria-label="Model id"
                        className="font-mono text-xs"
                        placeholder="model-id"
                        maxLength={100}
                        value={r.model}
                        invalid={!r.model.trim()}
                        onChange={(e) => update(r.key, { model: e.target.value })}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <SelectInput
                        aria-label={`Provider for ${name}`}
                        value={r.provider}
                        onChange={(e) => update(r.key, { provider: e.target.value as ModelProvider })}
                      >
                        {Object.entries(PROVIDER_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </SelectInput>
                    </td>
                    <td className="py-2 pr-2">
                      <AmountInput label={`Input price for ${name}`} value={r.input} onChange={(v) => update(r.key, { input: v })} />
                    </td>
                    <td className="py-2 pr-2">
                      <AmountInput label={`Output price for ${name}`} value={r.output} onChange={(v) => update(r.key, { output: v })} />
                    </td>
                    <td className="py-2 pr-2">
                      <AmountInput
                        label={`Cache-read price for ${name}`}
                        allowEmpty
                        placeholder="—"
                        value={r.cacheRead}
                        onChange={(v) => update(r.key, { cacheRead: v })}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <AmountInput
                        label={`Cache-write price for ${name}`}
                        allowEmpty
                        placeholder="—"
                        value={r.cacheWrite}
                        onChange={(v) => update(r.key, { cacheWrite: v })}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <TextInput
                        type="date"
                        aria-label={`Prices from date for ${name}`}
                        className="text-xs"
                        value={r.effectiveFrom}
                        onChange={(e) => update(r.key, { effectiveFrom: e.target.value })}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <SelectInput
                        aria-label={`Tier for ${name}`}
                        value={r.tier}
                        onChange={(e) => update(r.key, { tier: e.target.value as ModelTier | '' })}
                      >
                        <option value="">
                          Auto ({TIER_LABELS[autoTier(r.output)]})
                        </option>
                        {MODEL_TIERS.map((tier) => (
                          <option key={tier} value={tier}>
                            {TIER_LABELS[tier]}
                          </option>
                        ))}
                      </SelectInput>
                    </td>
                    <td className="py-2 pr-2">
                      <TextInput
                        aria-label={`Context window in tokens for ${name}`}
                        inputMode="numeric"
                        placeholder="From feed"
                        className="text-right tabular-nums"
                        value={r.contextWindow}
                        invalid={r.contextWindow.trim() !== '' && !(Number.isInteger(Number(r.contextWindow)) && Number(r.contextWindow) >= 1000)}
                        onChange={(e) => update(r.key, { contextWindow: e.target.value })}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <TextInput
                        aria-label={`Notes for ${name}`}
                        maxLength={200}
                        value={r.notes}
                        onChange={(e) => update(r.key, { notes: e.target.value })}
                      />
                    </td>
                    <td className="py-2 pr-2 text-right tabular-nums text-text-muted">
                      {used ? used.toLocaleString() : <span className="text-text-faint">—</span>}
                    </td>
                    <td className="py-2">
                      <IconButton
                        icon="fa-solid fa-trash-can"
                        label={`Remove ${name}`}
                        onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                      />
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={12} className="py-4 text-text-muted">
                    No models match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-text-muted">
          {rows.length} models · defaults are public list prices as of September 2026 — replace them with your contracted rates.
        </p>
      </Card>

      <SaveBar
        dirty={dirty}
        saving={saving}
        error={validationError ?? error}
        savedAt={savedAt}
        audit={settings.audit.models}
        onSave={handleSave}
        onDiscard={() => {
          setRows(toDraft(baseline));
          setValidationError(undefined);
          reset();
        }}
      />
    </div>
  );
}
